/**
 * Generated by @openapi-codegen
 *
 * @version v1
 */
import * as reactQuery from '@tanstack/react-query';
import { useConnectorContext, ConnectorContext } from './connector-context';
import type * as Fetcher from './connector-fetcher';
import { connectorFetch } from './connector-fetcher';
import type * as Schemas from './connector-schemas';

export type EchoError = Fetcher.ErrorWrapper<undefined>;

export type EchoVariables = ConnectorContext['fetcherOptions'];

export const fetchEcho = (variables: EchoVariables, signal?: AbortSignal) =>
    connectorFetch<string, EchoError, undefined, {}, {}, {}>({
        url: '/api/echo',
        method: 'get',
        ...variables,
        signal,
    });

export const useEcho = <TData = string>(
    variables: EchoVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<string, EchoError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<string, EchoError, TData>({
        queryKey: queryKeyFn({
            path: '/api/echo',
            operationId: 'echo',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchEcho({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type GetPatientsQueryParams = {
    series?: boolean;
};

export type GetPatientsError = Fetcher.ErrorWrapper<undefined>;

export type GetPatientsResponse = Schemas.PatientDTO[];

export type GetPatientsVariables = {
    queryParams?: GetPatientsQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetPatients = (
    variables: GetPatientsVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        GetPatientsResponse,
        GetPatientsError,
        undefined,
        {},
        GetPatientsQueryParams,
        {}
    >({ url: '/api/patients', method: 'get', ...variables, signal });

export const useGetPatients = <TData = GetPatientsResponse>(
    variables: GetPatientsVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            GetPatientsResponse,
            GetPatientsError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<GetPatientsResponse, GetPatientsError, TData>({
        queryKey: queryKeyFn({
            path: '/api/patients',
            operationId: 'getPatients',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetPatients({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type GetPatientPathParams = {
    patientId: string;
};

export type GetPatientQueryParams = {
    series?: boolean;
    instances?: boolean;
};

export type GetPatientError = Fetcher.ErrorWrapper<undefined>;

export type GetPatientVariables = {
    pathParams: GetPatientPathParams;
    queryParams?: GetPatientQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetPatient = (
    variables: GetPatientVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientDTO,
        GetPatientError,
        undefined,
        {},
        GetPatientQueryParams,
        GetPatientPathParams
    >({
        url: '/api/patients/{patientId}',
        method: 'get',
        ...variables,
        signal,
    });

export const useGetPatient = <TData = Schemas.PatientDTO>(
    variables: GetPatientVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Schemas.PatientDTO, GetPatientError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Schemas.PatientDTO, GetPatientError, TData>({
        queryKey: queryKeyFn({
            path: '/api/patients/{patientId}',
            operationId: 'getPatient',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetPatient({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type DownloadDicomSeriesPathParams = {
    studyId: string;
    seriesId: string;
};

export type DownloadDicomSeriesError = Fetcher.ErrorWrapper<undefined>;

export type DownloadDicomSeriesVariables = {
    pathParams: DownloadDicomSeriesPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDownloadDicomSeries = (
    variables: DownloadDicomSeriesVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        DownloadDicomSeriesError,
        undefined,
        {},
        {},
        DownloadDicomSeriesPathParams
    >({
        url: '/api/studies/{studyId}/series/{seriesId}.zip',
        method: 'get',
        ...variables,
        signal,
    });

export const useDownloadDicomSeries = <TData = Blob>(
    variables: DownloadDicomSeriesVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, DownloadDicomSeriesError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, DownloadDicomSeriesError, TData>({
        queryKey: queryKeyFn({
            path: '/api/studies/{studyId}/series/{seriesId}.zip',
            operationId: 'downloadDicomSeries',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchDownloadDicomSeries(
                { ...fetcherOptions, ...variables },
                signal
            ),
        ...options,
        ...queryOptions,
    });
};

export type DownloadDicomImagePathParams = {
    studyId: string;
    seriesId: string;
    instanceId: string;
};

export type DownloadDicomImageError = Fetcher.ErrorWrapper<undefined>;

export type DownloadDicomImageVariables = {
    pathParams: DownloadDicomImagePathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDownloadDicomImage = (
    variables: DownloadDicomImageVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        DownloadDicomImageError,
        undefined,
        {},
        {},
        DownloadDicomImagePathParams
    >({
        url: '/api/studies/{studyId}/series/{seriesId}/slices/{instanceId}.dcm',
        method: 'get',
        ...variables,
        signal,
    });

export const useDownloadDicomImage = <TData = Blob>(
    variables: DownloadDicomImageVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, DownloadDicomImageError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, DownloadDicomImageError, TData>({
        queryKey: queryKeyFn({
            path: '/api/studies/{studyId}/series/{seriesId}/slices/{instanceId}.dcm',
            operationId: 'downloadDicomImage',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchDownloadDicomImage(
                { ...fetcherOptions, ...variables },
                signal
            ),
        ...options,
        ...queryOptions,
    });
};

export type DownloadDicomImageBySOPPathParams = {
    patientID: string;
    modality: Schemas.Modality;
    sopInstanceUID: string;
};

export type DownloadDicomImageBySOPError = Fetcher.ErrorWrapper<undefined>;

export type DownloadDicomImageBySOPVariables = {
    pathParams: DownloadDicomImageBySOPPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDownloadDicomImageBySOP = (
    variables: DownloadDicomImageBySOPVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        DownloadDicomImageBySOPError,
        undefined,
        {},
        {},
        DownloadDicomImageBySOPPathParams
    >({
        url: '/api/studies/{patientID}/{modality}/{sopInstanceUID}.dcm',
        method: 'get',
        ...variables,
        signal,
    });

export const useDownloadDicomImageBySOP = <TData = Blob>(
    variables: DownloadDicomImageBySOPVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, DownloadDicomImageBySOPError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, DownloadDicomImageBySOPError, TData>({
        queryKey: queryKeyFn({
            path: '/api/studies/{patientID}/{modality}/{sopInstanceUID}.dcm',
            operationId: 'downloadDicomImageBySOP',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchDownloadDicomImageBySOP(
                { ...fetcherOptions, ...variables },
                signal
            ),
        ...options,
        ...queryOptions,
    });
};

export type UploadDICOMPathParams = {
    /**
     * @default default
     */
    target: string;
};

export type UploadDICOMError = Fetcher.ErrorWrapper<undefined>;

export type UploadDICOMResponse = {
    [key: string]: boolean;
};

export type UploadDICOMRequestBody = {
    files: Blob[];
};

export type UploadDICOMVariables = {
    body: UploadDICOMRequestBody;
    pathParams: UploadDICOMPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchUploadDICOM = (
    variables: UploadDICOMVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        UploadDICOMResponse,
        UploadDICOMError,
        UploadDICOMRequestBody,
        {},
        {},
        UploadDICOMPathParams
    >({ url: '/api/dicom/{target}', method: 'post', ...variables, signal });

export const useUploadDICOM = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            UploadDICOMResponse,
            UploadDICOMError,
            UploadDICOMVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        UploadDICOMResponse,
        UploadDICOMError,
        UploadDICOMVariables
    >({
        mutationFn: (variables: UploadDICOMVariables) =>
            fetchUploadDICOM({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type ListTargetsError = Fetcher.ErrorWrapper<undefined>;

export type ListTargetsResponse = string[];

export type ListTargetsVariables = ConnectorContext['fetcherOptions'];

export const fetchListTargets = (
    variables: ListTargetsVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        ListTargetsResponse,
        ListTargetsError,
        undefined,
        {},
        {},
        {}
    >({ url: '/api/dicom/targets', method: 'get', ...variables, signal });

export const useListTargets = <TData = ListTargetsResponse>(
    variables: ListTargetsVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            ListTargetsResponse,
            ListTargetsError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<ListTargetsResponse, ListTargetsError, TData>({
        queryKey: queryKeyFn({
            path: '/api/dicom/targets',
            operationId: 'listTargets',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchListTargets({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type SaveSessionError = Fetcher.ErrorWrapper<undefined>;

export type SaveSessionRequestBody = Schemas.SessionInput;

export type SaveSessionVariables = {
    body?: SaveSessionRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchSaveSession = (
    variables: SaveSessionVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        string,
        SaveSessionError,
        SaveSessionRequestBody,
        {},
        {},
        {}
    >({ url: '/api/session', method: 'post', ...variables, signal });

export const useSaveSession = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            string,
            SaveSessionError,
            SaveSessionVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        string,
        SaveSessionError,
        SaveSessionVariables
    >({
        mutationFn: (variables: SaveSessionVariables) =>
            fetchSaveSession({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetSessionPathParams = {
    sessionID: string;
};

export type GetSessionError = Fetcher.ErrorWrapper<undefined>;

export type GetSessionVariables = {
    pathParams: GetSessionPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetSession = (
    variables: GetSessionVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.Session,
        GetSessionError,
        undefined,
        {},
        {},
        GetSessionPathParams
    >({ url: '/api/session/{sessionID}', method: 'get', ...variables, signal });

export const useGetSession = <TData = Schemas.Session>(
    variables: GetSessionVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Schemas.Session, GetSessionError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Schemas.Session, GetSessionError, TData>({
        queryKey: queryKeyFn({
            path: '/api/session/{sessionID}',
            operationId: 'getSession',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetSession({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type DataTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type DataTaskProcessingRequestBody = Schemas.DataProcessingRequest;

export type DataTaskProcessingVariables = {
    body?: DataTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchDataTaskProcessing = (
    variables: DataTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        DataTaskProcessingError,
        DataTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/data', method: 'post', ...variables, signal });

export const useDataTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskOutput,
            DataTaskProcessingError,
            DataTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskOutput,
        DataTaskProcessingError,
        DataTaskProcessingVariables
    >({
        mutationFn: (variables: DataTaskProcessingVariables) =>
            fetchDataTaskProcessing({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type SegmentationTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type SegmentationTaskProcessingRequestBody =
    Schemas.SegmentationProcessingRequest;

export type SegmentationTaskProcessingVariables = {
    body?: SegmentationTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchSegmentationTaskProcessing = (
    variables: SegmentationTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        SegmentationTaskProcessingError,
        SegmentationTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/segmentation', method: 'post', ...variables, signal });

export const useSegmentationTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskOutput,
            SegmentationTaskProcessingError,
            SegmentationTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskOutput,
        SegmentationTaskProcessingError,
        SegmentationTaskProcessingVariables
    >({
        mutationFn: (variables: SegmentationTaskProcessingVariables) =>
            fetchSegmentationTaskProcessing({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type DoseTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type DoseTaskProcessingRequestBody = Schemas.DoseProcessingRequest;

export type DoseTaskProcessingVariables = {
    body?: DoseTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchDoseTaskProcessing = (
    variables: DoseTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        DoseTaskProcessingError,
        DoseTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/dose', method: 'post', ...variables, signal });

export const useDoseTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskOutput,
            DoseTaskProcessingError,
            DoseTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskOutput,
        DoseTaskProcessingError,
        DoseTaskProcessingVariables
    >({
        mutationFn: (variables: DoseTaskProcessingVariables) =>
            fetchDoseTaskProcessing({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type DoseCalcTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type DoseCalcTaskProcessingRequestBody =
    Schemas.DoseCalcProcessingRequest;

export type DoseCalcTaskProcessingVariables = {
    body?: DoseCalcTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchDoseCalcTaskProcessing = (
    variables: DoseCalcTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        DoseCalcTaskProcessingError,
        DoseCalcTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/dosecalc', method: 'post', ...variables, signal });

export const useDoseCalcTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskOutput,
            DoseCalcTaskProcessingError,
            DoseCalcTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskOutput,
        DoseCalcTaskProcessingError,
        DoseCalcTaskProcessingVariables
    >({
        mutationFn: (variables: DoseCalcTaskProcessingVariables) =>
            fetchDoseCalcTaskProcessing({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type ImageProcessingTaskProcessingError =
    Fetcher.ErrorWrapper<undefined>;

export type ImageProcessingTaskProcessingRequestBody =
    Schemas.ImageProcessingRequest;

export type ImageProcessingTaskProcessingVariables = {
    body?: ImageProcessingTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchImageProcessingTaskProcessing = (
    variables: ImageProcessingTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        ImageProcessingTaskProcessingError,
        ImageProcessingTaskProcessingRequestBody,
        {},
        {},
        {}
    >({
        url: '/api/tasks/imageprocessing',
        method: 'post',
        ...variables,
        signal,
    });

export const useImageProcessingTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskOutput,
            ImageProcessingTaskProcessingError,
            ImageProcessingTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskOutput,
        ImageProcessingTaskProcessingError,
        ImageProcessingTaskProcessingVariables
    >({
        mutationFn: (variables: ImageProcessingTaskProcessingVariables) =>
            fetchImageProcessingTaskProcessing({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type RegistrationTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type RegistrationTaskProcessingRequestBody =
    Schemas.RegProcessingRequest;

export type RegistrationTaskProcessingVariables = {
    body?: RegistrationTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchRegistrationTaskProcessing = (
    variables: RegistrationTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        RegistrationTaskProcessingError,
        RegistrationTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/registration', method: 'post', ...variables, signal });

export const useRegistrationTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskOutput,
            RegistrationTaskProcessingError,
            RegistrationTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskOutput,
        RegistrationTaskProcessingError,
        RegistrationTaskProcessingVariables
    >({
        mutationFn: (variables: RegistrationTaskProcessingVariables) =>
            fetchRegistrationTaskProcessing({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type TranslationTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type TranslationTaskProcessingRequestBody =
    Schemas.TranslationProcessingRequest;

export type TranslationTaskProcessingVariables = {
    body?: TranslationTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchTranslationTaskProcessing = (
    variables: TranslationTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        TranslationTaskProcessingError,
        TranslationTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/translation', method: 'post', ...variables, signal });

export const useTranslationTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskOutput,
            TranslationTaskProcessingError,
            TranslationTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskOutput,
        TranslationTaskProcessingError,
        TranslationTaskProcessingVariables
    >({
        mutationFn: (variables: TranslationTaskProcessingVariables) =>
            fetchTranslationTaskProcessing({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetTasksListError = Fetcher.ErrorWrapper<undefined>;

export type GetTasksListVariables = ConnectorContext['fetcherOptions'];

export const fetchGetTasksList = (
    variables: GetTasksListVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        GetTasksListError,
        undefined,
        {},
        {},
        {}
    >({ url: '/api/tasks', method: 'get', ...variables, signal });

export const useGetTasksList = <TData = Schemas.TaskOutput>(
    variables: GetTasksListVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            Schemas.TaskOutput,
            GetTasksListError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Schemas.TaskOutput, GetTasksListError, TData>({
        queryKey: queryKeyFn({
            path: '/api/tasks',
            operationId: 'getTasksList',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetTasksList({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type GetTaskStatusPathParams = {
    taskID: string;
};

export type GetTaskStatusError = Fetcher.ErrorWrapper<undefined>;

export type GetTaskStatusVariables = {
    pathParams: GetTaskStatusPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetTaskStatus = (
    variables: GetTaskStatusVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskOutput,
        GetTaskStatusError,
        undefined,
        {},
        {},
        GetTaskStatusPathParams
    >({ url: '/api/tasks/{taskID}', method: 'get', ...variables, signal });

export const useGetTaskStatus = <TData = Schemas.TaskOutput>(
    variables: GetTaskStatusVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            Schemas.TaskOutput,
            GetTaskStatusError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Schemas.TaskOutput, GetTaskStatusError, TData>({
        queryKey: queryKeyFn({
            path: '/api/tasks/{taskID}',
            operationId: 'getTaskStatus',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetTaskStatus({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type CancelTaskPathParams = {
    taskID: string;
};

export type CancelTaskError = Fetcher.ErrorWrapper<undefined>;

export type CancelTaskVariables = {
    pathParams: CancelTaskPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchCancelTask = (
    variables: CancelTaskVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        CancelTaskError,
        undefined,
        {},
        {},
        CancelTaskPathParams
    >({ url: '/api/tasks/{taskID}', method: 'delete', ...variables, signal });

export const useCancelTask = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            CancelTaskError,
            CancelTaskVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        CancelTaskError,
        CancelTaskVariables
    >({
        mutationFn: (variables: CancelTaskVariables) =>
            fetchCancelTask({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetTaskResultPathParams = {
    taskID: string;
    resultPath: string;
};

export type GetTaskResultQueryParams = {
    /**
     * @default false
     */
    skipReidentification?: boolean;
};

export type GetTaskResultError = Fetcher.ErrorWrapper<undefined>;

export type GetTaskResultVariables = {
    pathParams: GetTaskResultPathParams;
    queryParams?: GetTaskResultQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetTaskResult = (
    variables: GetTaskResultVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        GetTaskResultError,
        undefined,
        {},
        GetTaskResultQueryParams,
        GetTaskResultPathParams
    >({
        url: '/api/tasks/{taskID}/results/{resultPath}',
        method: 'get',
        ...variables,
        signal,
    });

export const useGetTaskResult = <TData = Blob>(
    variables: GetTaskResultVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, GetTaskResultError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, GetTaskResultError, TData>({
        queryKey: queryKeyFn({
            path: '/api/tasks/{taskID}/results/{resultPath}',
            operationId: 'getTaskResult',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetTaskResult({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type GetTaskErrorPathParams = {
    taskID: string;
    errorPath: string;
};

export type GetTaskErrorError = Fetcher.ErrorWrapper<undefined>;

export type GetTaskErrorVariables = {
    pathParams: GetTaskErrorPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetTaskError = (
    variables: GetTaskErrorVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        GetTaskErrorError,
        undefined,
        {},
        {},
        GetTaskErrorPathParams
    >({
        url: '/api/tasks/{taskID}/errors/{errorPath}',
        method: 'get',
        ...variables,
        signal,
    });

export const useGetTaskError = <TData = Blob>(
    variables: GetTaskErrorVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, GetTaskErrorError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, GetTaskErrorError, TData>({
        queryKey: queryKeyFn({
            path: '/api/tasks/{taskID}/errors/{errorPath}',
            operationId: 'getTaskError',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetTaskError({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type SaveDICOMToCacheError = Fetcher.ErrorWrapper<undefined>;

export type SaveDICOMToCacheRequestBody = {
    files: Blob[];
};

export type SaveDICOMToCacheVariables = {
    body: SaveDICOMToCacheRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchSaveDICOMToCache = (
    variables: SaveDICOMToCacheVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        SaveDICOMToCacheError,
        SaveDICOMToCacheRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/cache', method: 'put', ...variables, signal });

export const useSaveDICOMToCache = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Blob,
            SaveDICOMToCacheError,
            SaveDICOMToCacheVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Blob,
        SaveDICOMToCacheError,
        SaveDICOMToCacheVariables
    >({
        mutationFn: (variables: SaveDICOMToCacheVariables) =>
            fetchSaveDICOMToCache({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetProxiedPathParams = {
    rest: string;
};

export type GetProxiedError = Fetcher.ErrorWrapper<undefined>;

export type GetProxiedVariables = {
    pathParams: GetProxiedPathParams;
} & ConnectorContext['fetcherOptions'];

/**
 * Proxies a GET request to the backend server.
 */
export const fetchGetProxied = (
    variables: GetProxiedVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        GetProxiedError,
        undefined,
        {},
        {},
        GetProxiedPathParams
    >({ url: '/proxy/{rest}', method: 'get', ...variables, signal });

/**
 * Proxies a GET request to the backend server.
 */
export const useGetProxied = <TData = undefined>(
    variables: GetProxiedVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<undefined, GetProxiedError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<undefined, GetProxiedError, TData>({
        queryKey: queryKeyFn({
            path: '/proxy/{rest}',
            operationId: 'getProxied',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetProxied({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type PostProxiedPathParams = {
    rest: string;
};

export type PostProxiedError = Fetcher.ErrorWrapper<undefined>;

export type PostProxiedVariables = {
    pathParams: PostProxiedPathParams;
} & ConnectorContext['fetcherOptions'];

/**
 * Proxies a POST request to the backend server.
 */
export const fetchPostProxied = (
    variables: PostProxiedVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        PostProxiedError,
        undefined,
        {},
        {},
        PostProxiedPathParams
    >({ url: '/proxy/{rest}', method: 'post', ...variables, signal });

/**
 * Proxies a POST request to the backend server.
 */
export const usePostProxied = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            PostProxiedError,
            PostProxiedVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        PostProxiedError,
        PostProxiedVariables
    >({
        mutationFn: (variables: PostProxiedVariables) =>
            fetchPostProxied({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type PutProxiedPathParams = {
    rest: string;
};

export type PutProxiedError = Fetcher.ErrorWrapper<undefined>;

export type PutProxiedVariables = {
    pathParams: PutProxiedPathParams;
} & ConnectorContext['fetcherOptions'];

/**
 * Proxies a PUT request to the backend server.
 */
export const fetchPutProxied = (
    variables: PutProxiedVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        PutProxiedError,
        undefined,
        {},
        {},
        PutProxiedPathParams
    >({ url: '/proxy/{rest}', method: 'put', ...variables, signal });

/**
 * Proxies a PUT request to the backend server.
 */
export const usePutProxied = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            PutProxiedError,
            PutProxiedVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        PutProxiedError,
        PutProxiedVariables
    >({
        mutationFn: (variables: PutProxiedVariables) =>
            fetchPutProxied({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type DeleteProxiedPathParams = {
    rest: string;
};

export type DeleteProxiedError = Fetcher.ErrorWrapper<undefined>;

export type DeleteProxiedVariables = {
    pathParams: DeleteProxiedPathParams;
} & ConnectorContext['fetcherOptions'];

/**
 * Proxies a DELETE request to the backend server.
 */
export const fetchDeleteProxied = (
    variables: DeleteProxiedVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        DeleteProxiedError,
        undefined,
        {},
        {},
        DeleteProxiedPathParams
    >({ url: '/proxy/{rest}', method: 'delete', ...variables, signal });

/**
 * Proxies a DELETE request to the backend server.
 */
export const useDeleteProxied = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            DeleteProxiedError,
            DeleteProxiedVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        DeleteProxiedError,
        DeleteProxiedVariables
    >({
        mutationFn: (variables: DeleteProxiedVariables) =>
            fetchDeleteProxied({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type QueryOperation =
    | {
          path: '/api/echo';
          operationId: 'echo';
          variables: EchoVariables;
      }
    | {
          path: '/api/patients';
          operationId: 'getPatients';
          variables: GetPatientsVariables;
      }
    | {
          path: '/api/patients/{patientId}';
          operationId: 'getPatient';
          variables: GetPatientVariables;
      }
    | {
          path: '/api/studies/{studyId}/series/{seriesId}.zip';
          operationId: 'downloadDicomSeries';
          variables: DownloadDicomSeriesVariables;
      }
    | {
          path: '/api/studies/{studyId}/series/{seriesId}/slices/{instanceId}.dcm';
          operationId: 'downloadDicomImage';
          variables: DownloadDicomImageVariables;
      }
    | {
          path: '/api/studies/{patientID}/{modality}/{sopInstanceUID}.dcm';
          operationId: 'downloadDicomImageBySOP';
          variables: DownloadDicomImageBySOPVariables;
      }
    | {
          path: '/api/dicom/targets';
          operationId: 'listTargets';
          variables: ListTargetsVariables;
      }
    | {
          path: '/api/session/{sessionID}';
          operationId: 'getSession';
          variables: GetSessionVariables;
      }
    | {
          path: '/api/tasks';
          operationId: 'getTasksList';
          variables: GetTasksListVariables;
      }
    | {
          path: '/api/tasks/{taskID}';
          operationId: 'getTaskStatus';
          variables: GetTaskStatusVariables;
      }
    | {
          path: '/api/tasks/{taskID}/results/{resultPath}';
          operationId: 'getTaskResult';
          variables: GetTaskResultVariables;
      }
    | {
          path: '/api/tasks/{taskID}/errors/{errorPath}';
          operationId: 'getTaskError';
          variables: GetTaskErrorVariables;
      }
    | {
          path: '/proxy/{rest}';
          operationId: 'getProxied';
          variables: GetProxiedVariables;
      };
